import React, { useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import type { StatusType } from "../../../components/status-indicator/StatusIndicator";
import { useRateShotMutation, useDeleteShotRatingMutation } from "../../../app/services/shots";
import type { Shot } from "../../../app/services/shots";

interface RatingModalProps {
  shot: Shot;
  onClose: () => void;
}

const StarsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 0;
`;

const StarButton = styled.button<{ $active: boolean; $hovered: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.15);
  }

  .material-symbols-rounded {
    font-size: 40px;
    color: ${({ $active, $hovered }) =>
      $active || $hovered ? "#FFF000" : "#d0d0d0"};
    transition: color 0.15s ease;
  }
`;

const RatingModal: React.FC<RatingModalProps> = ({ shot, onClose }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [selectedRating, setSelectedRating] = useState<number>(shot.my_rating || 0);
  const [hoveredStar, setHoveredStar] = useState<number>(0);
  const [saveStatus, setSaveStatus] = useState<StatusType>("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [rateShot, { isLoading: isRating }] = useRateShotMutation();
  const [deleteShotRating, { isLoading: isDeleting }] = useDeleteShotRatingMutation();

  const isProcessing = isRating || isDeleting;
  const hasExistingRating = shot.my_rating != null && shot.my_rating > 0;

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const handleStarClick = useCallback(async (star: number) => {
    if (!projectId || isProcessing) return;
    setSelectedRating(star);
    setSaveStatus("saving");
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);

    try {
      await rateShot({
        project_id: projectId,
        shot_id: shot.id,
        rating: star,
      }).unwrap();
      setSaveStatus("saved");
      savedTimerRef.current = setTimeout(() => onClose(), 800);
    } catch {
      setSaveStatus("error");
      toast.error("評価の更新に失敗しました");
      savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
    }
  }, [projectId, shot.id, isProcessing, rateShot, onClose]);

  const handleDelete = useCallback(async () => {
    if (!projectId) return;
    setSaveStatus("deleting");
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);

    try {
      await deleteShotRating({
        project_id: projectId,
        shot_id: shot.id,
      }).unwrap();
      setSelectedRating(0);
      setSaveStatus("saved");
      savedTimerRef.current = setTimeout(() => onClose(), 800);
    } catch {
      setSaveStatus("error");
      toast.error("評価の取り消しに失敗しました");
      savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
    }
  }, [projectId, shot.id, deleteShotRating, onClose]);

  return (
    <Modal
      onClose={onClose}
      secondaryAction={
        hasExistingRating
          ? {
              label: "評価を取り消し",
              onClick: handleDelete,
              disabled: isProcessing,
              theme: "tertiary",
              variant: "outlined",
            }
          : undefined
      }
    >
      <StarsRow>
        {[1, 2, 3, 4, 5].map((star) => {
          const isActive = star <= selectedRating;
          const isHovered = hoveredStar > 0 && star <= hoveredStar;
          return (
            <StarButton
              key={star}
              $active={isActive}
              $hovered={isHovered}
              onClick={() => handleStarClick(star)}
              onMouseEnter={() => setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              disabled={isProcessing}
            >
              <span className="material-symbols-rounded">star</span>
            </StarButton>
          );
        })}
      </StarsRow>
      <StatusIndicator status={saveStatus} inModal />
    </Modal>
  );
};

export default RatingModal;
